import {
   Alert,
   API_ENDPOINTS,
   Button,
   customToast,
   FormInput,
   FormLabel,
   GasSafeEngineerResponse,
   GasSafeLookup,
   ImageType,
   LinkifyText,
   PostcodeLookup,
   replaceKeyWithValue,
   Roles,
   route,
   SelectOption,
   Stepper,
   StringHelpers,
   SubTitle,
   useDisabledContext,
   useLangContext,
   useQueries,
   useUserContext,
   Variants,
} from 'carrier-fe';
import { useEffect, useMemo, useRef, useState } from 'react';
import { StepProps } from '../../types/journey';
import { QUERY_KEYS, SYSTEM_TYPES } from '../../util/Constants';
import axios from 'axios';
import { useSearchParams } from 'react-router-dom';
import { InstallationFormData } from '../../pages/installation/journey';

interface InstallerOptions extends SelectOption {
   gas_safe_licence_card_number: string;
   system_types_status: {
      [key in SYSTEM_TYPES]: boolean | string;
   };
}

type SystemTypeKey = `${SYSTEM_TYPES}`;

const Section = ({ children }: { children: React.ReactNode }) => {
   return <div className="mb-4">{children}</div>;
};

function Step1({ form, loading, stepLang }: StepProps) {
   const [searchParams] = useSearchParams();
   const systemTypeFromParams = searchParams.get('type');
   const { setValues, store, errors, Input, setErrors } = form;
   const { crud, fields } = useLangContext();
   const { setDisabled, disabled } = useDisabledContext();
   const [isFetching, setIsFetching] = useState<boolean>(false);
   const [gasSafeNameMismatch, setGasSafeNameMismatch] = useState<boolean>(false);
   const [gasSafeResponses, setGasSafeResponses] = useState<{
      company: any;
      engineer: any;
   }>({
      company: null,
      engineer: null,
   });
   const [gasSafeLookupData, setGasSafeLookupData] = useState<GasSafeEngineerResponse | undefined>(
      undefined
   );
   const [showGasSafeFields, setShowGasSafeFields] = useState(
      systemTypeFromParams === SYSTEM_TYPES.GAS_BOILER ||
         form?.store?.system_type === SYSTEM_TYPES.GAS_BOILER
   );
   const [formError, setFormError] = useState<string[] | undefined>(undefined);

   const [selects, setSelects] = useState<{ [key: string]: InstallerOptions[] }>({
      installer_companies: [],
      installer_users: [],
   });
   const { user } = useUserContext();

   const { data, error, isLoading } = useQueries([
      {
         key: QUERY_KEYS.INSTALLER_COMPANIES,
         config: {
            method: 'GET',
            url: route(`${API_ENDPOINTS.GENERIC.SELECT.COMPANIES}?types[]=installer`),
         },
      },
   ]);

   useEffect(() => {
      // Handle API data and error
      if (data) {
          setSelects((prev) => ({
            ...prev,
            installer_companies: data.installer_companies?.data ?? [],
          }));
      }

      if (error) {
         Object.values(error).map((e) =>
            customToast({ title: (e?.response?.data as any)?.message, variant: Variants.Danger })
         );
      }
      let updatedValues = {};
      // Set default values if the user is an engineer
      if (user?.id && user?.role_name === Roles.ENGINEER) {
         updatedValues = {
            installer_user_id: user.id,
            installer_company_id: user?.individual?.company_id,
         };
      } else if (user?.id && user?.role_name === Roles.COMPANY_MANAGER) {
         updatedValues = {
            installer_company_id: user?.individual?.company_id,
         };
      }
      if (!form?.store?.system_type) {
         updatedValues = {
            ...updatedValues,
            system_type: systemTypeFromParams || '',
         };
      }
      setValues(updatedValues);
   }, [data, error, user]);

   useEffect(() => {
      if (store?.installer_company_id) {
         fetchInstallers();

      } else {
         setSelects((prev) => ({
            ...prev,
            installer_users: [],
         }));
      }
   }, [store?.installer_company_id]);

   const fetchInstallers = async () => {
      setIsFetching(true);
      try {
         const response = await axios.get(
            route(API_ENDPOINTS.GENERIC.SELECT.INSTALLERS, {
               companyId: store?.installer_company_id,
            })
         );
         setSelects((prev) => ({
            ...prev,
            installer_users: response?.data?.data ?? [],
         }));
      } catch (error: any) {
         customToast({
            title: error?.response?.data?.message || '',
            variant: Variants.Danger,
         });
         setSelects((prev) => ({
            ...prev,
            installer_users: [],
         }));
      } finally {
         setIsFetching(false);
      }
   };

   useEffect(() => {
      const selectedInstaller = selects.installer_users.find(
         (user) => user.value === form?.store?.installer_user_id
      );

      const isValidSystemType = (systemType: string): systemType is SystemTypeKey => {
         return Object.values(SYSTEM_TYPES).includes(systemType as SYSTEM_TYPES);
       };

      if (systemTypeFromParams && isValidSystemType(systemTypeFromParams)) {
         const systemTypeErrors = selectedInstaller?.system_types_status[systemTypeFromParams];

         if (typeof systemTypeErrors === "string") {
            setFormError([systemTypeErrors]);
         }
      }      

   }, [store?.installer_user_id]);

   const disableCompanyField = useMemo(
      () =>
         (user?.role_name === Roles.COMPANY_MANAGER || user?.role_name === Roles.ENGINEER) &&
         !!store?.installer_company_id,
      [store, user?.role_name]
   );
   const disableEngineerField = useMemo(
      () =>
         !store?.installer_company_id ||
         (user?.role_name === Roles.ENGINEER && !!store?.installer_user_id),
      [store, user?.role_name]
   );

   const disclaimers = useMemo(() => {
      const messages = { engineer: '', company: '' };
      if (user?.role_name === Roles.COMPANY_MANAGER) {
         messages.company = stepLang?.helper_texts?.installer_company || '';
      }
      if (user?.role_name === Roles.ENGINEER) {
         messages.company = stepLang?.helper_texts?.installer_company_individual || '';
         messages.engineer = stepLang?.helper_texts?.installer_individual || '';
      }

      return messages;
   }, [stepLang, user?.role_name]);

   // Show lookup if installer is selected and has no gas safe number and system type is gas boiler
   const showGasSafeLookup = useMemo(() => {
      const installerOption = selects.installer_users.find(
         (user) => user.value === form?.store?.installer_user_id
      );

      return (
         !!form?.store?.installer_user_id &&
         !installerOption?.gas_safe_licence_card_number &&
         (!systemTypeFromParams || systemTypeFromParams === SYSTEM_TYPES.GAS_BOILER)
      );
   }, [form?.store?.installer_user_id, selects.installer_users, systemTypeFromParams]);

   const checkNameMismatch = (lookupData: GasSafeEngineerResponse) => {
      const selectedEngineer = selects.installer_users.find(
         (user) => user.value === form?.store?.installer_user_id
      );
      if (selectedEngineer) {
         return (
            selectedEngineer.label?.toLocaleLowerCase() !==
            `${lookupData?.first_name} ${lookupData?.last_name}`.toLocaleLowerCase()
         );
      }
      return false;
   };

   const myAccountPageLink = useMemo(() => {
      const dashbaordUrl = process.env.REACT_APP_DASHBOARD_URL || '';
      return `${dashbaordUrl}/my-account`;
   }, []);

   const nameMismatchText = useMemo(() => {
      return (
         stepLang?.helper_texts?.gassafe_licence_card_will_be_compared ||
         'The details on the Gas Safe licence card will be checked against the registration details we have on record. Please make sure that the name in your VPlus account is identical to the name on your Gas Safe licence card. If your name does not match, please update it here :link .'
      );
   }, [stepLang]);

   const yesNoOptions = useMemo(
      () =>
         crud?.options?.boolean?.map((option: SelectOption) => ({
            ...option,
            value: Number(option.value) === 1 ? true : false,
         })),
      [crud?.options?.boolean]
   );

   const showNoneEngineerGasSafeFields = useMemo(
      () =>
         !showGasSafeFields
            ? true
            : !!gasSafeResponses?.engineer || !!store?.subcontractor_engineer_first_name,
      [gasSafeResponses?.engineer, showGasSafeFields, store?.subcontractor_engineer_first_name]
   );

   const showNoneCompanyGasSafeFields = useMemo(
      () =>
         !showGasSafeFields
            ? true
            : !!gasSafeResponses?.company || !!store?.subcontractor_company_name,
      [gasSafeResponses?.company, showGasSafeFields, store?.subcontractor_company_name]
   );

   const fetchGasSafeDetails = async (value: string | number, type: 'company' | 'engineer', findAddressId?: boolean) => {
      setErrors(null);
      setDisabled(true);

      const ENDPOINTS = {
         company: `${route(API_ENDPOINTS.GENERIC.GAS_SAFE.COMPANY.VIEW)}?gas_safe_number=${value}`,
         engineer: `${route(
            API_ENDPOINTS.GENERIC.GAS_SAFE.ENGINEER.VIEW
         )}?gas_safe_licence_card_number=${value}`,
      };

      let url = ENDPOINTS[type];

      if (findAddressId) url += '&find_address_id=1'

      try {
         const response = await axios.get(url);
         const gasSafe = { ...response.data.data };

         const companyValues: InstallationFormData = gasSafe && {
            subcontractor_address_line_1:
               gasSafe.address_line_1 || form?.store?.subcontractor_address_line_1 || '',
            subcontractor_address_line_2:
               gasSafe.address_line_2 || form?.store?.subcontractor_address_line_2 || '',
            subcontractor_address_line_3:
               gasSafe.address_line_3 || form?.store?.subcontractor_address_line_3 || '',
            subcontractor_town_city:
               gasSafe.town_city || form?.store?.subcontractor_town_city || '',
            subcontractor_state_county: gasSafe.state_county || form?.store?.state_county || '',
            subcontractor_postcode_zipcode:
               gasSafe.postcode_zipcode || form?.store?.postcode_zipcode || '',
            subcontractor_country_code_iso_3:
               gasSafe.country_code_iso_3 || form?.store?.subcontractor_country_code_iso_3 || '',
            subcontractor_company_name:
               gasSafe.name || form?.store?.subcontractor_company_name || '',
         };

         const engineerValues: InstallationFormData = gasSafe && {
            subcontractor_engineer_first_name: gasSafe.first_name ?? '',
            subcontractor_engineer_last_name: gasSafe.last_name ?? '',
         };

         if (type === 'company') {
            setValues(companyValues);
            setGasSafeResponses({ ...gasSafeResponses, company: gasSafe });
            customToast({
               title: response?.data?.message,
               variant: Variants.Success,
            });
         } else {
            setValues(engineerValues);
            setGasSafeResponses({ ...gasSafeResponses, engineer: gasSafe });
            customToast({
               title: response?.data?.message,
               variant: Variants.Success,
            });
         }
      } catch (error: any) {
         const message = error?.response?.data?.message || 'Error fetching details';
         customToast({ title: message, variant: Variants.Danger });

         setErrors((prevErrors: any) => ({
            ...prevErrors,
            ...error?.response?.data?.data?.errors,
         }));
      } finally {
         setDisabled(false);
      }
   };

   const companyGasSafeNumber = store?.subcontractor_company_gas_safe_number;
   const previousCompanyGasSafeNumber = useRef(companyGasSafeNumber);

   useEffect(() => {
      // The stepper unmounts inactive steps, so this runs again when the user
      // comes back to step 1. Only clear looked-up details when the number changes.
      if (previousCompanyGasSafeNumber.current === companyGasSafeNumber) {
         return;
      }

      previousCompanyGasSafeNumber.current = companyGasSafeNumber;

      setGasSafeResponses({
         company: null,
         engineer: null,
      });

      setValues({
         subcontractor_address_id: "",
         subcontractor_address_line_1: "",
         subcontractor_address_line_2: "",
         subcontractor_address_line_3: "",
         subcontractor_town_city: "",
         subcontractor_state_county: "",
         subcontractor_postcode_zipcode: "",
         subcontractor_country_code_iso_3: "",
         subcontractor_local_authority_code: "",
         subcontractor_sub_country_name: "",
         subcontractor_company_gas_safe_number_confirmed: false,
         subcontractor_engineer_gas_safe_licence_card_number: "",
         subcontractor_engineer_first_name: "",
         subcontractor_engineer_last_name: "",
         subcontractor_engineer_gas_safe_licence_card_number_confirmed: false,
      });

   }, [companyGasSafeNumber]);

   return (
      <Stepper.Step
         loading={isLoading || loading}
         title={stepLang?.title}
         description={stepLang?.description}
      >
         <div className="d-flex flex-column gap-4">
            <div>
               <FormInput
                  name={'installer_company_id'}
                  type="searchable-select"
                  label={StringHelpers.title(fields?.installer_company_id ?? 'Company')}
                  disabled={disableCompanyField || disabled}
                  options={selects.installer_companies}
                  value={form?.store?.installer_company_id}
                  onChange={(val: any) => {
                     setFormError(undefined);
                     form?.setValues({
                        installer_company_id: val,
                     })
                  }}
                  noMargin
                  errorMessages={form?.errors?.installer_company_id}
                  description={disableCompanyField ? disclaimers.company : ''}
               />
            </div>
           
            <FormInput
               type={'select'}
               name={'subcontractor_exists'}
               value={store?.subcontractor_exists}
               label={StringHelpers.title(
                  fields?.subcontractor_exists || 'Work was subcontracted'
               )}
               onChange={(value: string | boolean | string[] | ImageType | null) =>
                  setValues({
                     subcontractor_exists: typeof value === 'string' ? JSON.parse(value) : value,
                  })
               }
               noMargin
               options={yesNoOptions}
               disabled={disabled}
            />

            {!store?.subcontractor_exists && (
               <div>
                  <FormInput
                     name={'installer_user_id'}
                     type="searchable-select"
                     loading={isFetching}
                     label={StringHelpers.title(fields?.installer_user_id ?? 'Engineer')}
                     disabled={isFetching || disableEngineerField || disabled}
                     options={selects.installer_users}
                     value={form?.store?.installer_user_id}
                     onChange={(val: any) => {
                        setFormError(undefined);
                        form?.setValues({
                           installer_user_id: val,
                        });
                     }}
                     noMargin
                     errorMessages={form?.errors?.installer_user_id}
                     description={disableEngineerField ? disclaimers.engineer : ''}
                     noOptionsMessage={(inputValue) => {
                        if (isFetching) return crud?.buttons?.loading || 'Loading...';
                        
                        return inputValue?.trim()
                           ? `No results found for "${inputValue}"`
                           : null;
                     }}    
                  />
               </div>
            )}
            {formError && !store?.subcontractor_exists && (
               <Alert variant={Variants.Warning}>
                  {formError?.length > 1
                     ? formError.map((err, index) => <li key={index}>{err}</li>)
                     : formError[0]}
               </Alert>
            )}
            {!store?.subcontractor_exists && showGasSafeLookup && (
               <div>
                  <GasSafeLookup
                     displayCard
                     type={'engineer'}
                     onGasSafeNumberChange={(value) =>
                        form?.setValues({
                           gas_safe_licence_card_number: value,
                        })
                     }
                     hideConfirmed
                     disabled={disabled}
                     gasSafeNumber={form?.store?.gas_safe_licence_card_number}
                     errors={{
                        gasSafeNumber: form?.errors?.gas_safe_licence_card_number,
                     }}
                     labels={{
                        gasSafeNumber: fields?.gas_safe_licence_card_number,
                     }}
                     onGasSafeLookupChange={(lookupData) => {
                        if (!lookupData?.engineer) return;
                        setGasSafeLookupData(lookupData?.engineer);
                        const nameMismatch = checkNameMismatch(lookupData?.engineer);
                        setGasSafeNameMismatch(nameMismatch);
                     }}
                     content={
                        <>
                           {gasSafeNameMismatch && gasSafeLookupData && (
                              <Alert variant={Variants.Warning}>
                                 <LinkifyText
                                    text={replaceKeyWithValue(
                                       nameMismatchText,
                                       'link',
                                       myAccountPageLink
                                    )}
                                 />
                              </Alert>
                           )}
                        </>
                     }
                  />
               </div>
            )}

            {store?.subcontractor_exists && (
               <Section>
                  <div className="d-flex flex-column gap-1 mb-4">
                     <SubTitle
                        title={StringHelpers.title(
                           stepLang?.sub_sections?.[0]?.title || 'Subcontractor'
                        )}
                        className="m-0 w-100"
                        style={{ color: '#464C5E', fontSize: 32 }}
                     />
                  </div>
                  <FormLabel
                     label={StringHelpers.title(crud?.sub_titles?.company || 'Company')}
                     className="mb-3 w-100"
                     name="subcontractor_company_name"
                  />
                  <div className="d-flex flex-column gap-3">
                     {
                        <div className="d-flex align-items-start gap-2 justify-content-between">
                           <div className="flex-fill">
                              <FormInput
                                 noMargin
                                 type={'text'}
                                 value={store?.subcontractor_company_gas_safe_number ?? ''}
                                 name={'subcontractor_company_gas_safe_number'}
                                 label={StringHelpers.title(
                                    fields?.subcontractor_company_gas_safe_number ||
                                       'Company Gas Safe Number'
                                 )}
                                 onChange={(value) =>
                                    setValues({
                                       subcontractor_company_gas_safe_number: value as string,
                                       subcontractor_company_gas_safe_number_confirmed: false,
                                    })
                                 }
                                 errorMessages={
                                    errors?.gas_safe_number ||
                                    errors?.subcontractor_company_gas_safe_number
                                 }
                                 disabled={disabled}
                              />
                           </div>
                           <Button
                              label="Search"
                              variant={Variants.Dark}
                              type="SOLID"
                              size="Medium"
                              onClick={() => {
                                 fetchGasSafeDetails(
                                    store?.subcontractor_company_gas_safe_number,
                                    'company',
                                    true
                                 );
                              }}
                              disabled={disabled}
                           />
                        </div>
                     }
                     {(gasSafeResponses?.company || store.subcontractor_company_gas_safe_number_confirmed) &&
                        Input({
                           name: 'subcontractor_company_name',
                           label: StringHelpers.title(
                              fields?.subcontractor_company_name || 'Company Name'
                           ),
                           type: 'text',
                           visible: showNoneCompanyGasSafeFields,
                        })}

                     {((gasSafeResponses?.company && showNoneCompanyGasSafeFields) || store.subcontractor_company_gas_safe_number_confirmed) && (
                        <PostcodeLookup
                           data={{
                              address_id: store?.subcontractor_address_id || '',
                              sub_country_name: store?.subcontractor_sub_country_name || '',
                              address_line_1: store?.subcontractor_address_line_1 || '',
                              address_line_2: store?.subcontractor_address_line_2 || '',
                              address_line_3: store?.subcontractor_address_line_3 || '',
                              town_city: store?.subcontractor_town_city || '',
                              state_county: store?.subcontractor_state_county || '',
                              postcode_zipcode: store?.subcontractor_postcode_zipcode || '',
                              local_authority_code: store?.subcontractor_local_authority_code || '',
                              country_code_iso_3: store?.subcontractor_country_code_iso_3 || '',
                           }}
                           setData={(value) => {
                              setValues({
                                 subcontractor_address_id: value?.address_id,
                                 subcontractor_address_line_1: value?.address_line_1,
                                 subcontractor_address_line_2: value?.address_line_2,
                                 subcontractor_address_line_3: value?.address_line_3,
                                 subcontractor_town_city: value?.town_city,
                                 subcontractor_state_county: value?.state_county,
                                 subcontractor_postcode_zipcode: value?.postcode_zipcode,
                                 subcontractor_country_code_iso_3: value?.country_code_iso_3,
                                 subcontractor_local_authority_code: value?.local_authority_code,
                                 subcontractor_sub_country_name: value?.sub_country_name,
                              });
                           }}
                           errors={{
                              address_line_1: errors?.subcontractor_address_line_1,
                              address_line_2: errors?.subcontractor_address_line_2,
                              address_line_3: errors?.subcontractor_address_line_3,
                              town_city: errors?.subcontractor_town_city,
                              postcode_zipcode: errors?.subcontractor_postcode_zipcode,
                              country_code_iso_3: errors?.subcontractor_country_code_iso_3,
                           }}
                           hideLookup={gasSafeResponses?.company?.address_line_1}
                           overrideDisabledFields={{
                              country_code_iso_3: disabled || false,
                              postcode_zipcode: disabled || false,
                           }}
                           overrideLabel={
                              <FormLabel
                                 style={{ marginBottom: 0 }}
                                 label={crud?.sub_titles?.address || 'Address'}
                                 name={'subcontractor_address'}
                              />
                           }
                        />
                     )}

                     {store?.subcontractor_exists && (
                        <FormInput
                           type={'select'}
                           name={'subcontractor_company_gas_safe_number_confirmed'}
                           value={store?.subcontractor_company_gas_safe_number_confirmed}
                           label={StringHelpers.sentence(
                              fields?.gas_safe_confirmation_company ||
                                 "Can you confirm that the company's GasSafe details are correct?"
                           )}
                           onChange={(value: string | boolean | string[] | ImageType | null) =>
                              setValues({
                                 subcontractor_company_gas_safe_number_confirmed:
                                    typeof value === 'string' ? JSON.parse(value) : value,
                              })
                           }
                           errorMessages={errors?.subcontractor_company_gas_safe_number_confirmed}
                           options={yesNoOptions}
                           disabled={
                              (!gasSafeResponses?.company && !store?.subcontractor_company_name) ||
                              disabled
                           }
                        />
                     )}
                  </div>
               </Section>
            )}

            {store?.subcontractor_exists && store.subcontractor_company_gas_safe_number_confirmed && (
               <Section>
                  <FormLabel
                     label={StringHelpers.title(crud?.sub_titles?.engineer || 'Engineer')}
                     name="subcontractor_engineer"
                     className="mb-3 w-100"
                  />
                  <div className="d-flex flex-column gap-3">
                     {(gasSafeResponses?.company || store.subcontractor_company_gas_safe_number_confirmed) && (
                        <div className="d-flex align-items-start gap-2 justify-content-between">
                           <div className="flex-fill">
                              <FormInput
                                 noMargin
                                 type={'text'}
                                 value={
                                    store?.subcontractor_engineer_gas_safe_licence_card_number ?? ''
                                 }
                                 name={'subcontractor_engineer_gas_safe_licence_card_number'}
                                 label={StringHelpers.title(
                                    fields?.gas_safe_licence_card_number ||
                                       'Engineer Gas Safe licence card Number'
                                 )}
                                 onChange={(value) =>
                                    setValues({
                                       subcontractor_engineer_gas_safe_licence_card_number:
                                          value as string,
                                       subcontractor_engineer_gas_safe_licence_card_number_confirmed:
                                          false,
                                    })
                                 }
                                 errorMessages={
                                    errors?.gas_safe_licence_card_number ||
                                    errors?.subcontractor_engineer_gas_safe_licence_card_number
                                 }
                                 disabled={disabled}
                              />
                           </div>
                           <Button
                              label={StringHelpers.title(
                                 replaceKeyWithValue(
                                    crud?.placeholders?.search || 'Search :model',
                                    'model',
                                    ''
                                 )
                              )}
                              variant={Variants.Dark}
                              type="SOLID"
                              size="Medium"
                              onClick={() => {
                                 fetchGasSafeDetails(
                                    store?.subcontractor_engineer_gas_safe_licence_card_number,
                                    'engineer'
                                 );
                              }}
                              disabled={disabled}
                           />
                        </div>
                     )}

                     {(gasSafeResponses?.engineer || (store.subcontractor_engineer_first_name && store.subcontractor_engineer_last_name)) && (
                        <div className="row">
                           <div className="col-12 col-md-6">
                              {Input({
                                 name: 'subcontractor_engineer_first_name',
                                 label: StringHelpers.title(fields?.first_name || 'First Name'),
                                 type: 'text',
                                 visible: showNoneEngineerGasSafeFields,
                              })}
                           </div>
                           <div className="col-12 col-md-6">
                              {Input({
                                 name: 'subcontractor_engineer_last_name',
                                 label: StringHelpers.title(fields?.last_name || 'Last Name'),
                                 type: 'text',
                                 visible: showNoneEngineerGasSafeFields,
                              })}
                           </div>
                        </div>
                     )}

                     {(gasSafeResponses?.company || (store.subcontractor_engineer_first_name && store.subcontractor_engineer_last_name)) && (
                        <FormInput
                           type={'select'}
                           name={'subcontractor_engineer_gas_safe_licence_card_number_confirmed'}
                           value={
                              store?.subcontractor_engineer_gas_safe_licence_card_number_confirmed
                           }
                           label={StringHelpers.sentence(
                              fields?.gas_safe_confirmation_engineer ||
                                 'Can you confirm that the engineer’s GasSafe details are correct?'
                           )}
                           onChange={(value: string | boolean | string[] | ImageType | null) =>
                              setValues({
                                 subcontractor_engineer_gas_safe_licence_card_number_confirmed:
                                    typeof value === 'string' ? JSON.parse(value) : value,
                              })
                           }
                           errorMessages={
                              errors?.subcontractor_engineer_gas_safe_licence_card_number_confirmed
                           }
                           options={yesNoOptions}
                           disabled={
                              (!gasSafeResponses?.engineer &&
                                 !store?.subcontractor_engineer_first_name) ||
                              disabled
                           }
                        />
                     )}
                  </div>
               </Section>
            )}
         </div>
      </Stepper.Step>
   );
}

export default Step1;
